import { GetOrderReferencesStr } from '../../components/smartsend/item/OrderReferenceNumberUtils';
import { GetConsignmentFromItem } from '../../components/smartsend/panels/consignments/GetConsignmentFromItem';
import { GetDefaultVatStatus, GetAllowedVatStatus } from '../../components/smartsend/quotes/utils/GetVatStatuses';
import { SmartSendProvider } from '~/lib/consts/providers';
import { Customer, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { EoriNumber, IossNumber, IossNumberSource } from '../consts/ItemMetaConstants';
import { Individual, Registered } from '../consts/TaxStatusConsts';
import { SumNumber, onlyUnique } from '../helpers/ArrayHelper';
import { IossIsPrePaid, IossIsRequired } from '../helpers/IossUtils';
import { ItemExtrasAutoApplier } from '../helpers/ItemExtrasAutoApplier';
import { SanitiseStatus } from '../helpers/SanitiseStatus';
import { getMarketplaceTransactionProcessedDate } from '../helpers/SmartSendFilterApplier';
import { IsSmartSend } from '../helpers/SmartSendSummaryGenerator';

export const initialiseItem = (item: SmartSendItemApiModel) => {
  item.Created = new Date(item.Created);

  if (item.PreferredCollectionDate) {
    item.PreferredCollectionDate = new Date(item.PreferredCollectionDate);
    if (item.PreferredCollectionDate < new Date()) {
      item.PreferredCollectionDate = undefined;
    }
  }

  if (!item.Meta['ExportReason']) {
    item.Meta['ExportReason'] = { Type: 'String', Value: 'Sale' };
  }

  if (item.Recipient && !item.Recipient.VatStatus) {
    item.Recipient.VatStatus = GetDefaultVatStatus('Recipient');
  }

  if (item.Sender && !item.Sender.VatStatus) {
    item.Sender.VatStatus = GetDefaultVatStatus('Sender');
  }

  if (!item.ReferenceSummary) {
    item.ReferenceSummary = GetOrderReferencesStr(item);
  }

  if (!item.MarketplaceTransactionDate) {
    item.MarketplaceTransactionDate = getMarketplaceTransactionProcessedDate(item);
  }

  item.Consignment = GetConsignmentFromItem(item);
};

export const sanitiseItemForSave = (item: SmartSendItemApiModel) => {
  // Refresh the Created field to be a Date
  item.Created = new Date(item.Created);

  // Update Value
  if (item.MarketplaceOrderLines) {
    item.Value = SumNumber(item.MarketplaceOrderLines.map((x) => x.Value));
  }

  // Status
  item.Status = SanitiseStatus(item.Status);

  // Sort them tags
  if (item.Tags) {
    item.Tags = item.Tags.sort();
  }

  // Lowercase Emails
  if (item.Recipient?.Email) {
    item.Recipient.Email = item.Recipient.Email.toLowerCase();
  }
  if (item.Sender?.Email) {
    item.Sender.Email = item.Sender.Email.toLowerCase();
  }

  // Add Upsells as required
  ItemExtrasAutoApplier(item);
};

export const stateCustomerIsSender = (customer: Customer, sender: Customer) =>
  customer.Id === sender.Id &&
  customer.Name === sender.Name &&
  customer.CountryIsoCode === sender.CountryIsoCode &&
  customer.CountrySubdivision === sender.CountrySubdivision;

export const mapCustomer = (itemSender: Customer, savedCustomer: Customer): Customer | null => {
  if (savedCustomer) {
    // If the item sender has a name, email and phone, we will use that as the sender 'base'.
    if (itemSender?.Name && itemSender?.Email && itemSender?.Phone) {
      return {
        ...itemSender,
        CountryIsoCode: itemSender.CountryIsoCode || savedCustomer.CountryIsoCode || 'GBR',
        CountrySubdivision: itemSender.CountrySubdivision || savedCustomer.CountrySubdivision,
        Meta: itemSender.Meta || savedCustomer.Meta || {},
        Provider: itemSender.Provider || savedCustomer.Provider || SmartSendProvider,
        VatNumber: itemSender.VatNumber || savedCustomer.VatNumber || '',
        VatStatus: itemSender.VatStatus || savedCustomer.VatStatus || GetDefaultVatStatus('Sender'),
        Postcode: itemSender.Postcode || savedCustomer.Postcode,
        Name: itemSender.Name || savedCustomer.Name,
        County: itemSender.County || savedCustomer.County,
        Town: itemSender.Town || savedCustomer.Town,
        Locality: itemSender.Locality || savedCustomer.Locality,
        AddressLine1: itemSender.AddressLine1 || savedCustomer.AddressLine1,
        AddressLine2: itemSender.AddressLine2 || savedCustomer.AddressLine2,
        Phone: itemSender.Phone || savedCustomer.Phone,
        Email: itemSender.Email || savedCustomer.Email,
        Organisation: itemSender.Organisation || savedCustomer.Organisation,
        AccountName: itemSender.AccountName || savedCustomer.AccountName,
        PropertyKey: itemSender.PropertyKey || savedCustomer.PropertyKey
      };
    } else {
      return { ...savedCustomer };
    }
  }
  return null;
};

export const mapSenderToItem = (item: SmartSendItemApiModel, savedAddress: Customer) => {
  if (savedAddress && item?.Sender) {
    if (savedAddress.Meta) {
      // Only assign if the item is not pre-paid and the field is required by the validation rules.
      if (
        item.Meta &&
        savedAddress.Meta[IossNumber]?.Value &&
        !IossIsPrePaid(item.Meta[IossNumberSource]?.Value) &&
        IossIsRequired(item.State?.validation?.QuoteState)
      ) {
        item.Meta[IossNumber] = savedAddress.Meta[IossNumber];
      }
      if (savedAddress.Meta[EoriNumber]?.Value) {
        item.Sender.Meta[EoriNumber] = savedAddress.Meta[EoriNumber];
      }
    }
    if (savedAddress.VatNumber) {
      item.Sender.VatNumber = savedAddress.VatNumber;
    }
    if (savedAddress.VatStatus) {
      item.Sender.VatStatus = savedAddress.VatStatus;
    }
    if (item.Sender.VatNumber && item.Sender.VatStatus === Individual) {
      item.Sender.VatStatus = Registered;
    }

    if (!item.Sender.VatNumber) {
      item.Sender.VatStatus = GetAllowedVatStatus('Sender', item.Sender.VatStatus);
    }
  }
};

export const getIds = (ids: string) => {
  return (ids || '')
    .split('_')
    .filter((x) => !!x)
    .filter(onlyUnique);
};

export const getIdsByState = (states: string[], items: SmartSendItemApiModel[]) => {
  let o = '';
  items
    .filter((x) => x.State && states.includes(x.State.state))
    .forEach((x) => {
      o += `_${x.Id}`;
    });

  return o;
};

export const isQuotableItem = (item: SmartSendItemApiModel): boolean => {
  return (
    item &&
    IsSmartSend(item) &&
    item.MarketplaceStatus === 'Sold' &&
    item.Status === 'Active' &&
    item.BookingDetails?.BookingStatus !== 'Booked'
  );
};
